import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Button, Space, Spin, Select } from 'antd';
import {
    AppleOutlined,
    AndroidOutlined,
    DownloadOutlined,
    ArrowRightOutlined,
    GiftOutlined,
    StarOutlined,
    RocketOutlined,
    TrophyOutlined,
    ThunderboltOutlined,
    CrownOutlined,
    FireOutlined,
    DollarOutlined,
    CheckCircleFilled,
    SafetyCertificateOutlined,
    CustomerServiceOutlined,
    SwapOutlined,
    SettingOutlined,
    CarOutlined,
    LinkOutlined,
    DisconnectOutlined,
    PhoneOutlined,
    EnvironmentOutlined,
    ShoppingCartOutlined,
    DeliveredProcedureOutlined,
    ShopOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Banner, FeatureCard, AnimatedSection } from '../../components';
import { bannerService, settingsService, cityService } from '../../common/services';
import { useScrollAnimation } from '../../common/hooks';
import { FEATURES, GOOGLE_PLAY_URL, APP_STORE_URL, APP_STORE_AVAILABLE, APP_NAME } from '../../common/constants';
import Logo from '../../assets/logo-tigoon.png';
import AppScreenshot from '../../assets/app-home-2026.jpeg';
import BoutiqueScreenshot from '../../assets/app-boutique-2026.jpeg';
import LivreurScreenshot from '../../assets/app-livreur-2026.jpeg';
import CommercantScreenshot from '../../assets/app-commercant-2026.jpeg';
import '../../styles/service-showcase.css';
import './HomePage.css';

// Fonction pour générer les benefits avec les valeurs dynamiques
const getDriverBenefits = (settings) => [
    {
        icon: <GiftOutlined />,
        title: `${settings.bonusPremiereRecharge} Bonus Première Recharge`,
        description: `Anciens comme nouveaux chauffeurs reçoivent ${settings.bonusPremiereRecharge} en points sur votre première recharge.`,
        highlight: settings.bonusPremiereRecharge,
        tag: 'Bienvenue'
    },
    {
        icon: <RocketOutlined />,
        title: 'Nouveau Compte TIGOON + Yandex',
        description: 'Créez un nouveau compte TIGOON qui génère un nouveau compte Yandex.',
        highlight: settings.pointsNouveau,
        tag: 'Nouveau'
    },
    {
        icon: <StarOutlined />,
        title: 'Migration vers TIGOON',
        description: 'Ancien compte Yandex qui migre vers TIGOON.',
        highlight: settings.pointsAncien,
        tag: 'Migration'
    },
    {
        icon: <DollarOutlined />,
        title: 'Cashback sur Recharges',
        description: 'À chaque rechargement effectué par un compte Yandex lié à un compte TIGOON actif.',
        highlight: settings.cashbackRecharge,
        tag: 'Récurrent'
    },
    {
        icon: <TrophyOutlined />,
        title: `Bonus ${settings.seuilCourses}`,
        description: `Nouveaux comptes TIGOON ayant effectué ${settings.seuilCourses} en 1 semaine.`,
        highlight: settings.bonusMaxChallenge,
        tag: 'Challenge'
    },
    {
        icon: <FireOutlined />,
        title: `${settings.seuilHeures} de Course`,
        description: `Anciens et nouveaux chauffeurs atteignant ${settings.seuilHeures} de courses dans le mois.`,
        highlight: settings.bonusHeures,
        tag: 'Mensuel'
    },
    {
        icon: <CrownOutlined />,
        title: '0% Commission Partenaire',
        description: 'Profitez de 0% de commission partenaire.',
        highlight: settings.bonusCourses,
        tag: 'Premium'
    },
    {
        icon: <ThunderboltOutlined />,
        title: 'Lundi Bonus',
        description: `Premier rechargement de ${settings.montantMinLundi} le lundi.`,
        highlight: settings.bonusLundi,
        tag: 'Hebdo'
    }
];

// Valeurs par défaut pour les settings
const DEFAULT_SETTINGS = {
    bonusPremiereRecharge: '50%',
    pointsNouveau: '2 000 pts',
    pointsAncien: '1 000 pts',
    cashbackRecharge: '3%',
    bonusHeures: '2 000 pts',
    seuilHeures: '130h',
    bonusCourses: '2 000 pts',
    seuilCourses: '55 courses',
    bonusLundi: '2 000 pts',
    montantMinLundi: '10 000 F',
    bonusMaxChallenge: '4 000 pts'
};

// Icones par cle de service (les constantes ne peuvent pas contenir de JSX)
const SERVICE_ICONS = {
    'shopping-cart': <ShoppingCartOutlined />,
    'delivered-procedure': <DeliveredProcedureOutlined />,
    'shop': <ShopOutlined />,
    'thunderbolt': <ThunderboltOutlined />,
    'swap': <SwapOutlined />,
    'setting': <SettingOutlined />,
    'car': <CarOutlined />,
    'link': <LinkOutlined />,
    'disconnect': <DisconnectOutlined />,
    'phone': <PhoneOutlined />,
    'customer-service': <CustomerServiceOutlined />
};

// Ton clair associe a chaque couleur de service, pour construire les degrades
const SERVICE_LIGHT_TONE = {
    '#f5222d': '#ff7a45',
    '#7cb305': '#bae637',
    '#1d39c4': '#597ef7',
    '#fa8c16': '#ffc53d',
    '#52c41a': '#95de64',
    '#1890ff': '#69c0ff',
    '#13c2c2': '#5cdbd3',
    '#722ed1': '#b37feb',
    '#eb2f96': '#ff85c0',
    '#2f54eb': '#85a5ff',
    '#faad14': '#f5d89a'
};

// Degrade de l'icone, derive de la couleur du service
const gradientFor = (color) =>
    `linear-gradient(135deg, ${color} 0%, ${SERVICE_LIGHT_TONE[color] || color} 100%)`;

// Fond teinte (12%) pour les pastilles de la liste compacte
const tintFor = (color, alpha = 0.12) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Niveau 1 : les 3 univers. Niveau 2 : les services chauffeur.
// Une seule source de verite : la liste FEATURES (voir common/constants).
const UNIVERSES = FEATURES.filter((f) => f.universe);
const DRIVER_SERVICES = FEATURES.filter((f) => !f.universe);

// Section « Téléchargez l'application » : les écrans mis en vitrine.
// Ajouter une entrée ici suffit pour afficher un mockup de plus (max conseille : 3).
// L'ordre compte : l'entree du milieu est celle mise en avant (voir .showcase-3 dans le CSS).
// Les 3 ecrans reprennent les 3 publics cites juste a cote : chauffeurs, livreurs, commercants.
const DOWNLOAD_SHOWCASE = [
    { src: LivreurScreenshot, alt: 'Espace Livreur de l\'application TIGOON Connect' },
    { src: AppScreenshot, alt: 'Écran d\'accueil de l\'application TIGOON Connect' },
    { src: CommercantScreenshot, alt: 'Espace Commerçant de l\'application TIGOON Connect' }
];

const HomePage = () => {
    const [banners, setBanners] = useState({
        hero: [],
        middle: [],
        bottom: []
    });
    const [loading, setLoading] = useState(true);
    const [heroLoaded, setHeroLoaded] = useState(false);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [driverBenefits, setDriverBenefits] = useState(getDriverBenefits(DEFAULT_SETTINGS));
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);

    // Hooks pour les animations au scroll
    const [featuresRef, featuresVisible] = useScrollAnimation({ threshold: 0.1 });
    const [benefitsRef, benefitsVisible] = useScrollAnimation({ threshold: 0.1 });
    const [downloadRef, downloadVisible] = useScrollAnimation({ threshold: 0.1 });

    const fetchSettings = useCallback(async (cityCode) => {
        try {
            const response = await settingsService.getPublicSettings(cityCode);
            if (response?.success && response.data?.display) {
                setSettings(response.data.display);
                setDriverBenefits(getDriverBenefits(response.data.display));
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    }, []);

    useEffect(() => {
        fetchBanners();
        // Animation d'entrée douce
        setTimeout(() => setHeroLoaded(true), 100);

        const loadCities = async () => {
            const response = await cityService.getActiveCities();
            if (response?.success && response.data?.length > 0) {
                setCities(response.data);
                const defaultCity = response.data.find(c => c.code?.toUpperCase() === 'ABJ') || response.data[0];
                setSelectedCity(defaultCity.code);
                fetchSettings(defaultCity.code);
            } else {
                fetchSettings();
            }
        };
        loadCities();
    }, [fetchSettings]);

    const handleCityChange = (cityCode) => {
        setSelectedCity(cityCode);
        fetchSettings(cityCode);
    };

    const fetchBanners = async () => {
        try {
            const response = await bannerService.getAll();
            if (response?.success && response.data) {
                setBanners(response.data);
            }
        } catch (error) {
            console.error('Error fetching banners:', error);
        } finally {
            setLoading(false);
        }
    };

    // Rendu de la section hero statique (fallback)
    const renderStaticHero = () => (
        <section className={`hero-section ${heroLoaded ? 'hero-loaded' : ''}`}>
            <div className="hero-bg-pattern"></div>
            <div className="container">
                <Row gutter={[48, 48]} align="middle">
                    <Col xs={24} md={14}>
                        <div className={`hero-content ${heroLoaded ? 'animate-in' : ''}`}>
                            <div className="hero-badge animate-item" style={{ animationDelay: '0.1s' }}>
                                <RocketOutlined /> Chauffeurs &middot; Livreurs &middot; Commerçants
                            </div>
                            <h1 className="animate-item" style={{ animationDelay: '0.2s' }}>
                                <span className="text-gold">TIGOON Connect.</span>
                                <br />
                                Tous vos services
                                <br />
                                en une seule app.
                            </h1>
                            <p className="hero-description animate-item" style={{ animationDelay: '0.3s' }}>
                                Courses, livraisons, boutique, assistance : gérez votre activité et vos finances au même endroit. Profitez de bonus exclusifs !
                            </p>
                            <div className="hero-features animate-item" style={{ animationDelay: '0.4s' }}>
                                <div className="hero-feature">
                                    <CheckCircleFilled /> {settings.bonusPremiereRecharge} bonus sur 1ère recharge
                                </div>
                                <div className="hero-feature">
                                    <CheckCircleFilled /> Jusqu'à {settings.bonusMaxChallenge} offerts
                                </div>
                                <div className="hero-feature">
                                    <CheckCircleFilled /> {settings.cashbackRecharge} cashback sur recharges
                                </div>
                                <div className="hero-feature">
                                    <CheckCircleFilled /> 0% commission possible
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} md={10}>
                        <div className={`hero-phone-container ${heroLoaded ? 'animate-in' : ''}`}>
                            <div className="hero-phones">
                                {/* Second écran (Boutique), en retrait derrière l'écran principal */}
                                <div className="hero-phone-mockup phone-secondary" aria-hidden="true">
                                    <div className="phone-screen-image">
                                        <img src={BoutiqueScreenshot} alt="" className="app-screenshot" />
                                    </div>
                                </div>
                                <div className="hero-phone-mockup phone-primary">
                                    <div className="phone-screen-image">
                                        <img src={AppScreenshot} alt="TIGOON Connect App" className="app-screenshot" />
                                    </div>
                                </div>
                            </div>
                            <div className="floating-card card-1">
                                <GiftOutlined />
                                <span>+{settings.bonusPremiereRecharge} Bonus</span>
                            </div>
                            <div className="floating-card card-2">
                                <TrophyOutlined />
                                <span>{settings.bonusMaxChallenge}</span>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </section>
    );

    // Rendu de la section hero dynamique avec bannières
    const renderDynamicHero = () => (
        <section className="hero-section hero-banner-section">
            <div className="container">
                <Banner banners={banners.hero} type="hero" />
            </div>
        </section>
    );

    return (
        <div className="home-page">
            {/* Hero Section - Dynamique si bannières hero existent, sinon statique */}
            {!loading && banners.hero?.length > 0 ? renderDynamicHero() : renderStaticHero()}

            {/* Features Section - Nouveau Design */}
            <section
                ref={featuresRef}
                className={`features-section-new ${featuresVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    {/* Header */}
                    <div className={`features-header ${featuresVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}>
                        <span className="features-badge">Fonctionnalités</span>
                        <h2>
                            Tous les services <span className="text-gold">dans une seule appli</span>
                        </h2>
                        <p className="features-subtitle">
                            Découvrez toutes les fonctionnalités qui simplifient le quotidien des chauffeurs, des livreurs et des commerçants
                        </p>
                    </div>

                    {/* Niveau 1 : les 3 univers */}
                    <div className="universe-grid">
                        {UNIVERSES.map((universe, index) => (
                            <div
                                key={universe.key}
                                className={`universe-card ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                                style={{ transitionDelay: `${0.1 + index * 0.08}s` }}
                            >
                                <div className="universe-icon" style={{ background: gradientFor(universe.color) }}>
                                    {SERVICE_ICONS[universe.icon]}
                                </div>
                                <h3>{universe.title}</h3>
                                <p className="universe-description">{universe.description}</p>
                                <ul className="universe-points">
                                    {universe.points.map((point) => (
                                        <li key={point}>
                                            <CheckCircleFilled style={{ color: universe.color }} />
                                            <span>{point}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {/* Niveau 2 : les services chauffeur, en liste compacte */}
                    <div className="driver-services">
                        <div className="driver-services-title">
                            <span>Et aussi, pour les chauffeurs</span>
                        </div>
                        <div className="driver-services-grid">
                            {DRIVER_SERVICES.map((service, index) => (
                                <div
                                    key={service.key}
                                    className={`driver-service ${featuresVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}
                                    style={{ transitionDelay: `${0.35 + index * 0.05}s` }}
                                >
                                    <div className="driver-service-icon" style={{ background: tintFor(service.color), color: service.color }}>
                                        {SERVICE_ICONS[service.icon]}
                                    </div>
                                    <div className="driver-service-text">
                                        <h4>{service.title}</h4>
                                        <p>{service.shortDescription}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Driver Benefits Section */}
            <section
                ref={benefitsRef}
                className={`home-benefits-section ${benefitsVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`benefits-header ${benefitsVisible ? 'animate-fade-down is-visible' : 'animate-fade-down'}`}>
                        <h2>
                            <span className="text-gold">Gagnez plus</span> avec TIGOON Connect
                        </h2>
                        <p>Découvrez tous les avantages exclusifs réservés à nos chauffeurs partenaires</p>
                        {cities.length > 0 && (
                            <div className="city-selector">
                                <EnvironmentOutlined className="city-selector-icon" />
                                <Select
                                    value={selectedCity}
                                    onChange={handleCityChange}
                                    style={{ width: 180 }}
                                    suffixIcon={null}
                                >
                                    {cities.map(city => (
                                        <Select.Option key={city.code} value={city.code}>
                                            {city.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        )}
                    </div>
                    <Row gutter={[24, 24]}>
                        {driverBenefits.map((benefit, index) => (
                            <Col xs={24} sm={12} md={6} key={index}>
                                <div
                                    className={`benefit-card ${benefitsVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                                    style={{ transitionDelay: `${0.1 + index * 0.1}s` }}
                                >
                                    <div className="benefit-tag">{benefit.tag}</div>
                                    <div className="benefit-icon">{benefit.icon}</div>
                                    <div className="benefit-highlight">{benefit.highlight}</div>
                                    <h3 className="benefit-title">{benefit.title}</h3>
                                    <p className="benefit-description">{benefit.description}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </section>

            {/* Middle Banners */}
            {!loading && banners.middle?.length > 0 && (
                <section className="banners-section">
                    <div className="container">
                        <Banner banners={banners.middle} type="middle" />
                    </div>
                </section>
            )}

            {/* Download Section */}
            <section
                ref={downloadRef}
                className={`download-section ${downloadVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <Row gutter={[48, 48]} align="middle">
                        <Col xs={24} md={12}>
                            <div className={`download-content ${downloadVisible ? 'animate-fade-right is-visible' : 'animate-fade-right'}`}>
                                <h2>Téléchargez l'application</h2>
                                <p>
                                    Rejoignez la communauté TIGOON Connect et profitez de tous
                                    les avantages offerts à nos partenaires : chauffeurs, livreurs
                                    et commerçants.
                                </p>
                                <ul className="download-features">
                                    <li className="animate-list-item" style={{ animationDelay: '0.1s' }}>Courses, livraisons et boutique</li>
                                    <li className="animate-list-item" style={{ animationDelay: '0.2s' }}>Transferts et SOS Crédit</li>
                                    <li className="animate-list-item" style={{ animationDelay: '0.3s' }}>Promotions exclusives</li>
                                    <li className="animate-list-item" style={{ animationDelay: '0.4s' }}>Assistance et dépannage 24/7</li>
                                </ul>
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className={`phone-mockup showcase-${DOWNLOAD_SHOWCASE.length} ${downloadVisible ? 'animate-fade-left is-visible' : 'animate-fade-left'}`}>
                                {DOWNLOAD_SHOWCASE.map((shot, index) => (
                                    <div key={shot.alt} className={`phone-frame showcase-phone showcase-phone-${index + 1}`}>
                                        <div className="phone-screen-image">
                                            <img src={shot.src} alt={shot.alt} className="app-screenshot" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </div>
            </section>

            {/* Bottom Banners */}
            {!loading && banners.bottom?.length > 0 && (
                <section className="banners-section bottom-banners">
                    <div className="container">
                        <Banner banners={banners.bottom} type="bottom" />
                    </div>
                </section>
            )}

            {/* Fixed Download Bar */}
            <div className="fixed-download-bar">
                <div className="download-bar-card">
                    <span className="download-bar-text">
                        Téléchargez l'application TIGOON Connect
                    </span>
                    <div className="download-bar-badges">
                        <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="store-badge-small">
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                                alt="Google Play"
                                height="40"
                            />
                        </a>
                        {APP_STORE_AVAILABLE ? (
                            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="store-badge-small">
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                                    alt="App Store"
                                    height="40"
                                />
                            </a>
                        ) : (
                            <div className="store-badge-coming-soon">
                                <AppleOutlined />
                                <span>Bientôt disponible</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;
